import React, { useId } from 'react';
import unescapeHtmlEntities from 'lodash-es/unescape';
import styled from 'styled-components';

import Popover from '@rover/components/src/callouts/Popover';
import {
  EducationalOccupationalCredential,
  MicrodataHTMLAttributes,
  useEnableSchema,
} from '@rover/components/src/microdata';

import { extractTextContent, fireBadgeEvent, SitterBadgeProps } from './SitterBadge.common';

const StyledPopover = styled(Popover)`
  padding: 0 !important;
  width: 276px !important;
`;

const PopoverContentWrapper = styled.div`
  font-size: ${({ theme }) => theme.text['100'].fontSize};
`;

const PopoverContentTitle = styled.div`
  background-color: ${({ theme }) => theme.background.secondary};
  border-bottom: 1px solid #ececec;
  font-weight: ${({ theme }) => theme.display['500'].fontWeight};
  padding: ${({ theme }) => `${theme.space['3x']} 14px`};
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
`;

const PopoverContentBody = styled.div`
  padding: ${({ theme }) => `${theme.space['2x']} 14px`};
  font-weight: normal;
`;

const StyledImage = styled.img`
  margin-right: 5px;
`;

type PopoverContentProps = {
  title: string;
  content: string;
};

const PopoverContent: React.FC<PopoverContentProps> = ({ title, content }) => (
  <PopoverContentWrapper>
    {/* Badge content has HTML which is defined by us and cannot be modified by user */}
    <PopoverContentTitle
      dangerouslySetInnerHTML={{
        __html: unescapeHtmlEntities(title),
      }}
    />
    <PopoverContentBody
      dangerouslySetInnerHTML={{
        __html: unescapeHtmlEntities(content),
      }}
    />
  </PopoverContentWrapper>
);

// `PopoverContent` is only in the DOM when the trigger element is being hovered,
// so we need to render the content separately so it can be scraped
const PopoverContentAsMicrodata: React.FC<PopoverContentProps & MicrodataHTMLAttributes> = ({
  title,
  content,
  ...props
}) => (
  <EducationalOccupationalCredential
    /* Badge content has HTML which is defined by us and cannot be modified by user */
    name={extractTextContent(unescapeHtmlEntities(title))}
    description={extractTextContent(unescapeHtmlEntities(content))}
    {...props}
  />
);

const badgeHover = (slug: string): void => {
  fireBadgeEvent(slug);
};

const SitterBadge: React.FC<SitterBadgeProps> = ({
  className,
  content,
  imageUrl,
  onChange,
  slug,
  popoverPlacement = Popover.Placement.TOP,
  title,
  width = 16,
  ...props
}) => {
  const tooltipId = useId();
  const enableSchema = useEnableSchema();
  return (
    /* We prefer not to disable jsx-a11y callouts, but this is just stopping click events propagating */
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
    <span onClick={(e) => e.stopPropagation()} className={className}>
      {enableSchema && <PopoverContentAsMicrodata title={title} content={content} {...props} />}
      <StyledPopover
        id={tooltipId}
        role="tooltip"
        content={<PopoverContent title={title} content={content} />}
        placement={popoverPlacement}
        onChange={onChange}
        toggleOnFocusOrBlur
        usePortal
        data-testid="styled-popover"
      >
        <StyledImage
          src={imageUrl}
          alt={title}
          width={width}
          tabIndex={0}
          onMouseEnter={() => badgeHover(slug)}
          aria-describedby={tooltipId}
          data-testid="styled-image"
        />
      </StyledPopover>
    </span>
  );
};

export default SitterBadge;
